import _ from 'underscore';

import {assert, assertAndContinue} from 'Asserts';
import * as benchlingUtil from 'BenchlingUtil';
import count from 'util/count';

import areAminoAcidsValid from './areAminoAcidsValid';
import _extinctionCoefficient, {ExtinctionCoefficientOptions} from './extinctionCoefficient';
import getAminoAcidFrequency from './getAminoAcidFrequency';
import _getChargeAtPH from './getChargeAtPH';
import getInstabilityIndex from './getInstabilityIndex';
import _isoelectricPoint from './isoelectricPoint';
import {HYDROPHOBICITY_VALUES} from './ProteinColoringConstants';
import ProteinConstants, {
  AllAminoAcidSLA,
  getAminoAcidWeights,
  INSTABILITY_INDEX_THRESHOLD,
} from './ProteinConstants';

const HYDROGEN_WEIGHT = 1.00794;
function getWaterWeight(): number {
  return 18.015;
}

export {areAminoAcidsValid, getAminoAcidFrequency, getInstabilityIndex, INSTABILITY_INDEX_THRESHOLD};

function _canonicalizeAminoAcids(aminoAcids: string): string {
  const upperAminoAcids = aminoAcids.toUpperCase();
  assert(areAminoAcidsValid(upperAminoAcids), 'Cannot canonicalize invalid amino acids');
  return upperAminoAcids;
}

export function getMolecularWeight(aminoAcids: string, isCysReduced: boolean = true): number {
  const upperAminoAcids = _canonicalizeAminoAcids(aminoAcids);

  // Total AA sequence weight
  // is the sum of the amino acid, less a water for every bond between AAs (water
  // is produced in the formation of the amide bond)
  // An amide bond joins each amino acid, so there are N-1 amide bonds
  const baseAminoAcidWeight = benchlingUtil.sum(
    upperAminoAcids,
    (aa: AllAminoAcidSLA) => getAminoAcidWeights()[aa]
  );

  const totalAminoAcidWeight = baseAminoAcidWeight - (upperAminoAcids.length - 1) * getWaterWeight();

  if (isCysReduced) {
    return totalAminoAcidWeight;
  } else {
    const cysteineCount = count(Array.from(upperAminoAcids), 'C');
    return totalAminoAcidWeight - Math.floor(cysteineCount / 2) * 2.0 * HYDROGEN_WEIGHT;
  }
}

export function getIsoelectricPoint(aminoAcids: string): number {
  return _isoelectricPoint(_canonicalizeAminoAcids(aminoAcids));
}

export function getChargeAtPH(aminoAcids: string, pH: number): number {
  const upperAminoAcids = aminoAcids.toUpperCase();
  assert(areAminoAcidsValid(upperAminoAcids), 'Cannot get charge for invalid amino acids');
  return _getChargeAtPH(upperAminoAcids, pH);
}

export function getRelativeHydrophobicity(aminoAcid: string): number | null {
  if (aminoAcid === '-') {
    return null;
  } else {
    return HYDROPHOBICITY_VALUES[aminoAcid.toUpperCase() as keyof typeof HYDROPHOBICITY_VALUES] || 0;
  }
}

export const LOWEST_ISOELECTRIC_AMINO_ACID = 'd';
export const HIGHEST_ISOELECTRIC_AMINO_ACID = 'r';

// Map of isoelectric points of single amino acids, scaled linearly between 0 and 1.
// Handle gaps for alignments.
// This computation is done only once because the isoelectricPoint function is designed for sequences of
// multiple amino acids, and is thus relatively inefficient for single amino acids.
const _relativeIsoelectricPointMap: {[aminoAcid: string]: number | null} = _.object(
  ProteinConstants.ALIGNMENT_AMINO_ACIDS,
  ProteinConstants.ALIGNMENT_AMINO_ACIDS.map((aminoAcid) => {
    if (aminoAcid === '-') {
      return null;
    } else {
      const lowest = getIsoelectricPoint(LOWEST_ISOELECTRIC_AMINO_ACID);
      const highest = getIsoelectricPoint(HIGHEST_ISOELECTRIC_AMINO_ACID);
      return (getIsoelectricPoint(aminoAcid) - lowest) / (highest - lowest);
    }
  })
);

export function getRelativeIsoelectricPoint(aminoAcid: string): number | null {
  assertAndContinue(
    ProteinConstants.ALIGNMENT_AMINO_ACIDS.indexOf(aminoAcid.toUpperCase()) >= 0,
    'Cannot get pI of invalid amino acid'
  );
  return _relativeIsoelectricPointMap[aminoAcid.toUpperCase()];
}

export function getAbsorbance(
  aminoAcids: string,
  extinctionCoefficientOptions: ExtinctionCoefficientOptions
): number {
  const extinctionCoefficient = getExtinctionCoefficient(aminoAcids, extinctionCoefficientOptions);
  const mw = getMolecularWeight(aminoAcids, extinctionCoefficientOptions.assumeCysReduced);
  return extinctionCoefficient / mw;
}

export function getExtinctionCoefficient(
  aminoAcids: string,
  extinctionCoefficientOptions: ExtinctionCoefficientOptions
): number {
  return _extinctionCoefficient(aminoAcids, extinctionCoefficientOptions);
}
